/**
 * HintTile - Shows hint text when player steps on it
 */

import Phaser from "phaser";
import { BaseTile, TextureDescriptor } from "./BaseTile";
import { TILE } from "../constants/gameConstants";
import { TileIndex } from "../types";
import { GAME_CONFIG } from "../config";

export class HintTile extends BaseTile {
  constructor({ col, row, id }: { col: number; row: number; id?: string }) {
    super(id ?? `hint-${col}-${row}`, col, row);
  }

  tileType(): TileIndex {
    return TILE.HINT;
  }

  textureDescriptor(): TextureDescriptor {
    return {
      type: "procedural",
      generator: (scene: Phaser.Scene, key: string) => {
        const size = GAME_CONFIG.tileSize;
        const graphics = scene.add.graphics();

        // Block body (grass green)
        graphics.fillStyle(0x5a9a5a, 1);
        graphics.fillRect(0, 0, size, size);

        // Top-left highlight (lighter grass)
        graphics.fillStyle(0x6ab06a, 1);
        graphics.fillRect(0, 0, size, 6);
        graphics.fillRect(0, 0, 6, size);

        // Bottom-right shadow (darker grass)
        graphics.fillStyle(0x4a8a4a, 1);
        graphics.fillRect(0, size - 6, size, 6);
        graphics.fillRect(size - 6, 0, 6, size);

        // Corner accents (even darker grass)
        graphics.fillStyle(0x3a7a3a, 1);
        graphics.fillRect(0, 0, 6, 6);
        graphics.fillRect(size - 6, 0, 6, 6);
        graphics.fillRect(0, size - 6, 6, 6);
        graphics.fillRect(size - 6, size - 6, 6, 6);

        const cx = size / 2;
        const cy = size / 2;

        // Yellow circle in center
        graphics.fillStyle(0xffff00, 1);
        graphics.fillCircle(cx, cy, 14);

        // Black outline for yellow circle
        graphics.lineStyle(2, 0x000000, 1);
        graphics.strokeCircle(cx, cy, 14);

        // Black question mark inside yellow circle
        graphics.fillStyle(0x000000, 1);

        // ? top curve (smaller to fit in circle)
        graphics.fillRect(cx - 5, cy - 8, 10, 3);
        graphics.fillRect(cx + 3, cy - 5, 3, 5);
        graphics.fillRect(cx - 3, cy, 6, 3);
        graphics.fillRect(cx - 3, cy + 3, 3, 4);

        // ? dot
        graphics.fillRect(cx - 3, cy + 9, 3, 3);

        // Black outline for block
        graphics.lineStyle(2, 0x000000, 1);
        graphics.strokeRect(0, 0, size, size);

        graphics.generateTexture(this.textureKey(), size, size);
        graphics.destroy();
      },
    };
  }

  clone(): HintTile {
    const copy = new HintTile({ col: this.col, row: this.row, id: this.id });
    copy.sprite = this.sprite;
    return copy;
  }

  toJSON(): Record<string, any> {
    return { type: "hint", id: this.id, col: this.col, row: this.row };
  }
}
